/**
 * @file NotFoundBox.js
 * @description A React component that displays a box indicating that no data was found.
 *
 * @param {string} backgroundColor - The background color of the box. Default is '#f8f9fa'.
 * @param {string} title - The title of the box.
 * @param {string} description - The description of the box.
 * @param {string} icon - The icon to display ('magnifier' or 'calendar-x'). Default is 'magnifier'.
 * @param {string} width - The width of the box. Default is '576px'.
 * @param {string} actionButtonText - Optional text for the action button.
 * @param {function} onActionButtonClick - Optional callback for action button click.
 * @param {string} actionButtonIcon - Optional icon class for the action button (e.g., 'bi bi-plus-lg').
 *
 * @returns {JSX.Element} The rendered NotFoundBox component.
 */
import PropTypes from 'prop-types';
import NotFoundMagnifier from '../svg/NotFoundMagnifier';
import CalendarX from '../svg/CalendarX';
import styles from './NotFoundBox.module.scss';

const NotFoundBox = ({
    backgroundColor = '#f8f9fa',
    title,
    description,
    icon = 'magnifier',
    width = '576px',
    actionButtonText = null,
    onActionButtonClick = null,
    actionButtonIcon = null,
}) => {
    const showActionButton = actionButtonText && onActionButtonClick;
    const isAssetIcon = icon === 'asset';
    const isUserAddIcon = icon === 'user-add';
    const widthClassName =
        width === '576px' ? styles.widthDefault : styles.widthCustom;
    const backgroundClassName =
        backgroundColor === '#f0f4ff'
            ? styles.backgroundPrimary
            : styles.backgroundDefault;

    return (
        <div
            className={`${styles.container} ${backgroundClassName} ${widthClassName}`}
        >
            {isAssetIcon ? (
                <i className={`bi bi-image-fill ${styles.assetIcon}`}></i>
            ) : (
                <div className={styles.iconWrapper}>
                    {icon === 'magnifier' && (
                        <div className={backgroundClassName}>
                            <NotFoundMagnifier />
                        </div>
                    )}
                    {icon === 'calendar-x' && <CalendarX />}
                    {isUserAddIcon && (
                        <i
                            className={`bi bi-person-fill-add ${styles.userAddIcon}`}
                        ></i>
                    )}
                </div>
            )}
            <p className={styles.title}>{title}</p>
            <p
                className={`${styles.description} ${showActionButton ? styles.descriptionWithAction : ''}`}
            >
                {description}
            </p>
            {showActionButton && (
                <button
                    type="button"
                    onClick={onActionButtonClick}
                    className="button-base ing-button-primary-left m-auto mt-3"
                >
                    {actionButtonIcon && (
                        <i className={`${actionButtonIcon} me-2`} />
                    )}
                    {actionButtonText}
                </button>
            )}
        </div>
    );
};

NotFoundBox.propTypes = {
    backgroundColor: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    icon: PropTypes.oneOf(['magnifier', 'calendar-x', 'user-add', 'asset']),
    width: PropTypes.string,
    actionButtonText: PropTypes.string,
    onActionButtonClick: PropTypes.func,
    actionButtonIcon: PropTypes.string,
};

export default NotFoundBox;
